import { TextInput } from '@components/shared/forms';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import RichTextEditorInput from '@components/shared/forms/RichTextEditorInput';
import { useFormikContext } from 'formik';
import { AdminMicrosite } from '@root/types';
import TextColorSelector from '../../TextColorSelector';
import { ContentBlockWrapper, ContentBlockProps } from '..';
import { isSinglePageLayout, useMicrositeFormContext } from '../../utils';
import ColorPicker from '../../ColorPicker';

export default function Contact({ id, index, anchorSlug }: ContentBlockProps) {
  const { colorGroups, resetKey, microsite, changeFontColorWithColorChange, sendPreviewTo } =
    useMicrositeFormContext();
  const flags = useFlags();
  const { t } = useTranslation('admin');
  const { values } = useFormikContext<Partial<AdminMicrosite>>();

  const sectionTitlesAreRichText = flags['market.building-descriptions-support-rich-text'];

  return (
    <ContentBlockWrapper
      contentBlockId={id}
      title={
        flags['market-office.flexible-layouts-on-microsite']
          ? t('microsite.sections.leasingContacts')
          : t('microsite.sections.contactLeasingContacts')
      }
      section="contactLeasingContacts"
      onClick={() =>
        sendPreviewTo({
          page: isSinglePageLayout(values.layout!) ? 'home' : 'contact',
          element: anchorSlug,
        })
      }
      index={index}
    >
      <ColorPicker
        colorGroups={colorGroups}
        name="contactsBackgroundColor"
        labelText={t('microsite.backgroundColor')}
        data-testid="contact-background-color-picker"
        onChange={changeFontColorWithColorChange('contactsTextColor')}
      />
      {flags['market-office.text-color-selector'] && <TextColorSelector name="contactsTextColor" />}
      <ColorPicker
        colorGroups={colorGroups}
        name="contactsBorderColor"
        labelText={t('microsite.borderColor')}
        hasTransparentOption
      />
      {sectionTitlesAreRichText ? (
        <RichTextEditorInput
          name="contactsTitle"
          labelText={t('microsite.sectionTitle')}
          hideLabel
          editorClassName="min-h-[100px] bg-white"
          excludeMenuItems={
            flags['market.microsite-font-sizing-on-editors']
              ? ['bulletList']
              : ['bulletList', 'textSize']
          }
          initialContent={microsite.contactsTitle}
          defaultTextSize={
            flags['market.microsite-font-sizing-on-editors'] ? 'header32' : undefined
          }
          resetKey={resetKey}
        />
      ) : (
        <TextInput name="contactsTitle" labelText={t('microsite.sectionTitle')} />
      )}
    </ContentBlockWrapper>
  );
}
